import styled from '@emotion/styled';
import { useTranslations } from 'use-intl';

import { Skeleton } from '@/ui';

import { RendererOption } from './project-response-renderer.type';

interface Props {
  type: RendererOption;
  options: {
    id: string;
    label: string;
    value: string;
    count: number;
  }[];
  totalResponses: number;
  selectedOption?: string;
  selectedValues?: readonly string[];
  useResponseTotal?: boolean;
  loading: boolean;
}

const ResultBar = styled.div<{ width: number }>`
  width: ${(props) => props.width}%;
`;

export default function ProjectResponsePollResult({
  options,
  selectedOption,
  selectedValues,
  useResponseTotal = false,
  loading,
  type,
  totalResponses,
}: Props) {
  const t = useTranslations('Usability');

  if (loading) {
    const rowCount = Math.max(options.length, 3);
    return (
      <div
        data-test='poll-result-loading'
        className='inline-flex w-full max-w-160 flex-col items-start justify-center gap-2 rounded-2xl bg-white p-4 outline-1 -outline-offset-1 outline-gray-200 outline-solid'
      >
        {Array.from({ length: rowCount }).map((_, i) => (
          <Skeleton
            key={`poll-skeleton-row-${i}`}
            className='h-12 w-full rounded-lg'
          />
        ))}
        <Skeleton className='h-6 w-24 rounded-md' />
      </div>
    );
  }
  const totalCount = options.reduce((acc, curr) => acc + curr.count, 0);

  // Calculate raw percentages and track the rounding error
  const rawPercentages = options.map((option) =>
    Object.assign({}, option, {
      rawPercentage: totalCount > 0 ? (option.count / totalCount) * 100 : 0,
    })
  );

  // Calculate rounded percentages and track the rounding error
  const roundedPercentages = rawPercentages.map((item) =>
    Object.assign({}, item, {
      roundedPercentage: Math.round(item.rawPercentage),
      roundingError: Math.round(item.rawPercentage) - item.rawPercentage,
    })
  );

  // Sort by rounding error to distribute the error to the items that were rounded the most
  const sortedByError = roundedPercentages.toSorted(
    (a, b) => a.roundingError - b.roundingError
  );

  // Calculate the total rounding error
  const totalRounded = roundedPercentages.reduce(
    (sum, item) => sum + item.roundedPercentage,
    0
  );
  const error = 100 - totalRounded;

  // Distribute the error to the items that were rounded the most
  if (error !== 0) {
    const adjustment = totalCount === 0 ? 0 : Math.abs(error);
    const direction = error > 0 ? 1 : -1;

    for (let i = 0; i < adjustment; i++) {
      const index = direction > 0 ? i : sortedByError.length - 1 - i;
      sortedByError[index].roundedPercentage += direction;
    }
  }

  // Map back to original order
  const finalPercentages = options.map((option) => {
    const item = sortedByError.find((item) => item.id === option.id);
    const responsePercentage =
      totalResponses > 0
        ? Math.round((option.count / totalResponses) * 100)
        : 0;
    return {
      ...option,
      percentage: useResponseTotal
        ? responsePercentage
        : (item?.roundedPercentage ?? 0),
    };
  });

  return (
    <div
      data-test='poll-result'
      className='inline-flex w-full max-w-160 flex-col items-start justify-center gap-2 rounded-2xl bg-white p-4 outline-1 -outline-offset-1 outline-gray-200 outline-solid'
    >
      {finalPercentages.map((option) => {
        const isSelected =
          selectedValues?.includes(option.value) ??
          option.value === selectedOption;
        const denominator = useResponseTotal ? totalResponses : totalCount;
        const barWidth =
          denominator > 0
            ? Math.min(100, (option.count / denominator) * 100)
            : 0;

        return (
          <div
            className='group self-stretch rounded-[9px] bg-linear-to-r from-rose-400 to-indigo-400 p-px data-[selected=false]:bg-none'
            data-selected={isSelected}
            key={option.id}
            data-test='poll-result-bar'
          >
            <div className='relative flex size-full items-center justify-center gap-2 overflow-hidden rounded-lg bg-gray-50 p-3'>
              <ResultBar
                className='absolute top-0 left-0 h-full animate-fill-in-from-left bg-linear-to-r from-rose-100 to-indigo-100 transition-all group-data-[selected=true]:from-rose-200 group-data-[selected=true]:to-indigo-200'
                width={barWidth}
              />
              <div className='z-10 min-w-0 flex-1 font-sans wrap-break-word text-gray-900'>
                {type === 'rating' ? option.value : option.label}
              </div>
              <div className='z-10 font-sans'>{option.percentage}%</div>
            </div>
          </div>
        );
      })}
      <div
        data-test='poll-result-votes'
        className='min-h-6 w-full justify-start font-sans text-sm/normal'
      >
        {t('pollVotes', { count: totalResponses })}
      </div>
    </div>
  );
}
